/**
 * The survey list: what a row of `GET /survey/templates` says, and the two groups KANON draws it in.
 *
 * Moved here from `pages/reports/model` on 8 October 2026, when the client card's „Przypisz
 * ankietę" (`features/assign-survey`) became its second reader. `/raporty` re-exports it.
 *
 * ── The mandatory one is a GROUP, not a row with a flag ──────────────────────────────────────
 * KANON `Raporty` puts the intake under its own eyebrow — „Obowiązkowa" — above „Twoje ankiety",
 * and the two are different objects wearing one card: the intake has no cadence, cannot be paused,
 * cannot be deleted and is filled once at the client's first sign-in, while a check-in has a rhythm,
 * a status and a roster. Returning them as one list with `isMandatory` on it would put that
 * distinction in every consumer; returning two groups puts it here, once.
 *
 * The intake sorts FIRST because it is the first group, not because of a comparator. There is at
 * most one of it — `SurveyKind.Intake` is the trainer's single onboarding form — and a response that
 * somehow carried two would keep the first and drop nothing: the second lands in the check-ins,
 * where it is visible rather than silently missing.
 *
 * ── The order of the check-ins is the COLLATOR's ─────────────────────────────────────────────
 * By title, with the locale's own collator, so „Ćwiczenia" sorts where a Polish reader expects it
 * and not after „Z". The locale arrives as a parameter for the reason `planFreshness` takes the
 * clock as one: a rule that reads the ambient language cannot be tested against a second one.
 *
 * ── Every field is read off the wire, never composed ─────────────────────────────────────────
 * `questionCount` and `assignedClientCount` are the backend's figures and arrive with the list, which
 * is what lets a card state its size before any second read answers. `assignedCustomerIds` is the
 * 17 September addition that makes the client facet and the avatar stack possible without a read per
 * row; it is optional on the wire, and a row that does not carry it is a row with no faces rather
 * than an error.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import type {
  TrainWithMeModulesSurveysDtosResponsesSurveyTemplateListResponse as SurveyTemplateListResponse,
  TrainWithMeModulesSurveysDtosResponsesSurveyTemplateSummary as SurveyTemplateSummary,
  TrainWithMeModulesSurveysDtosSurveyCadenceView as SurveyCadenceView,
} from '~/shared/api/generated/model';

/** The `t` this module takes as a parameter; it imports no i18next. */
export type TranslateKey = (key: string, options?: Record<string, unknown>) => string;

/** The three rhythms a check-in can have — the backend's own spelling, carried as a string. */
export type SurveyCadence = 'Day' | 'Week' | 'Month';

function isSurveyCadence(value: unknown): value is SurveyCadence {
  return value === 'Day' || value === 'Week' || value === 'Month';
}

/** One card in the left column. */
export interface SurveyRow {
  readonly id: number;
  readonly title: string;
  /** `true` for the intake — the one form a client cannot be without. */
  readonly mandatory: boolean;
  /**
   * `true` for the public form's own template (public-form R4): listed beside the intake, edited
   * here like it, and never paused, assigned or deleted from this screen. Absent reads as `false`.
   */
  readonly leadForm?: boolean;
  /** `false` is „wstrzymana": the form exists and is asked of nobody until it is resumed. */
  readonly active: boolean;
  readonly questionCount: number;
  /** The backend's figure, which arrives with the list and waits for nothing. */
  readonly assignedCount: number;
  /** Who has it. Empty where the row did not carry the field at all — see the header. */
  readonly assignedCustomerIds: readonly number[];
  /** `null` for the intake, which is filled once rather than repeated. */
  readonly cadence: SurveyCadence | null;
  /** 0 = Sunday … 6 = Saturday, as the backend numbers it. `null` unless the cadence is weekly. */
  readonly dayOfWeek: number | null;
  /** 1 … 31. `null` unless the cadence is monthly. */
  readonly dayOfMonth: number | null;
}

/** The two groups, in the order KANON stacks them. */
export interface SurveyGroups {
  /** `null` while the list has not answered, and for a trainer whose intake read failed. */
  readonly intake: SurveyRow | null;
  /** The public form's template; absent or `null` for a trainer who has not published one. */
  readonly leadForm?: SurveyRow | null;
  readonly checkIns: readonly SurveyRow[];
}

function cadenceOf(cadence: SurveyCadenceView | null | undefined): SurveyCadence | null {
  return isSurveyCadence(cadence?.frequency) ? cadence.frequency : null;
}

function rowOf(summary: SurveyTemplateSummary): SurveyRow | null {
  // An id is the row's whole identity — it is what the detail read asks for and what the editor is
  // addressed by — so a summary without one is dropped rather than drawn as a card that leads
  // nowhere.
  if (typeof summary.id !== 'number') return null;

  const cadence = cadenceOf(summary.cadence);
  return {
    id: summary.id,
    title: summary.title ?? '',
    mandatory: summary.isMandatory === true,
    leadForm: summary.kind === 'LeadForm',
    // A row that does not say is treated as ACTIVE: the field is a pause switch, and defaulting a
    // silence to „wstrzymana" would tell the trainer their form had stopped.
    active: summary.isActive !== false,
    questionCount: summary.questionCount ?? 0,
    assignedCount: summary.assignedClientCount ?? 0,
    assignedCustomerIds: summary.assignedCustomerIds ?? [],
    cadence,
    dayOfWeek: cadence === 'Week' ? (summary.cadence?.dayOfWeek ?? null) : null,
    dayOfMonth: cadence === 'Month' ? (summary.cadence?.dayOfMonth ?? null) : null,
  };
}

/** Split the list into the mandatory form and the trainer's own, each in its drawn order. */
export function surveyGroups(
  response: SurveyTemplateListResponse | null | undefined,
  locale: string,
): SurveyGroups {
  const rows = (response?.templates ?? []).map(rowOf).filter((row): row is SurveyRow => row !== null);

  const mandatory = rows.filter((row) => row.mandatory && row.leadForm !== true);
  const intake = mandatory[0] ?? null;
  const leadForm = rows.find((row) => row.leadForm === true) ?? null;
  const collator = new Intl.Collator(locale);
  const checkIns = rows
    .filter((row) => row !== intake && row.leadForm !== true)
    .sort((left, right) => collator.compare(left.title, right.title));

  return { intake, leadForm, checkIns };
}

/** Every row, in the order the column draws them — what the selection and the facets walk. */
export function surveyRowsInOrder(groups: SurveyGroups): readonly SurveyRow[] {
  return [
    ...(groups.intake === null ? [] : [groups.intake]),
    ...(groups.leadForm == null ? [] : [groups.leadForm]),
    ...groups.checkIns,
  ];
}

/**
 * The weekday chip's word.
 *
 * The backend numbers weekdays 0 = Sunday … 6 = Saturday; `web:weekdays.short` is keyed 1 = Poniedziałek
 * … 7 = Niedziela, which is ISO. The conversion is here rather than at the two call sites that need
 * it, because two readings of one number is how a survey came to say Monday on one surface and
 * Sunday on another.
 */
function weekdayShort(dayOfWeek: number, t: TranslateKey): string {
  const iso = dayOfWeek === 0 ? 7 : dayOfWeek;
  return t(`web:weekdays.short.${String(iso)}`);
}

/**
 * The cadence chip: „codziennie", „co tydz · pon", „co mies · 1.".
 *
 * A cadence with no day names the rhythm alone. That is a real answer rather than a defect — the
 * intake has no cadence at all, and a check-in whose day the backend did not carry still has a
 * rhythm worth stating — so it gets its own word instead of an interpolation with a hole in it.
 */
export function cadenceLabel(row: SurveyRow, t: TranslateKey): string | null {
  if (row.cadence === null) return null;
  if (row.cadence === 'Day') return t('web:reports.cadence.day');
  if (row.cadence === 'Week') {
    return row.dayOfWeek === null
      ? t('web:reports.cadence.weekPlain')
      : t('web:reports.cadence.week', { day: weekdayShort(row.dayOfWeek, t) });
  }
  return row.dayOfMonth === null
    ? t('web:reports.cadence.monthPlain')
    : t('web:reports.cadence.month', { day: row.dayOfMonth });
}

/**
 * „aktywna" or „wstrzymana" — never „wysyłki", which the owner rejected as unreadable.
 *
 * The intake has no status: it is mandatory and cannot be paused, so the card says what it is
 * („Wymagana · MoveBuddy") rather than whether it is running.
 */
export function statusLabel(row: SurveyRow, t: TranslateKey): string | null {
  if (row.mandatory || row.leadForm === true) return null;
  return row.active ? t('web:reports.status.active') : t('web:reports.status.paused');
}

/** „7 pytań" — the count and its noun, declined by i18next's own plural rules for the locale. */
export function questionCountLabel(row: SurveyRow, t: TranslateKey): string {
  return t('web:reports.surveys.questionCount', { count: row.questionCount });
}

/** „12 klientów" — the roster figure the card states before any face has arrived. */
export function assignedCountLabel(row: SurveyRow, t: TranslateKey): string {
  return t('web:reports.surveys.assignedCount', { count: row.assignedCount });
}
